import * as React from "react";
import {cx} from "emotion";

import "./city.scss";

import {cbc}                    from "@Common/react/callback-chain";
import {cs}                     from "@Common/react/chain-services";
import {consumeContext}         from "@Common/react/context";
import {Load2}                  from "@Common/react/load2";
import {stateChangeNestedValue} from "@Common/react/state-change-nested-value";
import {Static2}                from "@Common/react/static-2";
import {UseState}               from "@Common/react/use-state";
import {Watch}                  from "@Common/react/watch";
import {ErrorHighlighter}       from "@Common/ui-components/light/error-highlighter";
import {scrollIntoView}         from "@Common/ui-components/light/scroll-into-view";
import {SearchableSelect}       from "@Common/ui-components/searchable-select/searchable-select";

import {ZipCodes} from "./zip-codes/zip-codes";

export const City = ({next}) => cs(
    ["apiError", (_, next) => UseState({next})],
    ["data", (_, next) => UseState({
        initValue: {},
        next,
    })],
    consumeContext("apis"),
    ["allCities", ({data, apis}, next) => Load2({
        _key: data.value?.state,
        fetch: () => cbc(data.value?.state, [
            (_, next) => !_ ? null : next(_),
            (_      ) => apis.next.location.getCities(_),
        ]),
        next,
    })],
    ["allZipCodes", ({data, apis}, next) => {
        const params = !data.value?.state || !data.value?.city ? null : {state: data.value?.state, city: data.value?.city};
        return Load2({
            _key: JSON.stringify(params),
            fetch: () => params && apis.next.location.getZipCodes(params),
            next,
        });
    }],
    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],

    ({allZipCodes, data, apiError}, next) => <>
        {next()}

        {Watch({
            value: allZipCodes.value,
            onChanged: (all) => {
                if (all) {
                    stateChangeNestedValue(data, ["zipCodes"], () => all);
                    apiError.onChange(null);
                }
            },
        })}
    </>,

    ({allZipCodes, allCities, data, eh, domRef, apiError}) => next({
        valid: data.value && data.value.state && data.value.city && data.value.zipCodes?.length,
        getSearchRequest: () => data.value && ({
            city: data.value.city,
            state: data.value.state,
            zipCodes: data.value.zipCodes?.join(","),
        }),
        highlightErrors: () => {
            eh.start();
            scrollIntoView(domRef.get());
        },
        handleApiError: (error) => {
            apiError.onChange({error});
            eh.start();
            scrollIntoView(domRef.get());
        },
        clearApiError: () => apiError.onChange(null),
        render: ({showErrors}) => (
            <div className="city-8p9">
                <div className="left">
                    <div className={cx("form", eh.css)} ref={domRef.set} >
                        <div className="line">
                            <div className="label" xd-qa-type="label" xd-qa-name="New Homeowners State">State</div>
                            <div className="control">
                                {SearchableSelect({
                                    list: require("../../../../../../../../../common/us-states.json"),
                                    getLabel: (s) => s.name,
                                    isSelected: (s) => s.abbreviation === data.value?.state,
                                    onSelect: (s) => {
                                        data.change((_) => ({
                                            state: s?.abbreviation,
                                        }));
                                        apiError.onChange(null);
                                    },
                                    onClose: () => {
                                        if (!showErrors.value.includes("state") && !data.value?.state) {
                                            showErrors.onChange([...showErrors.value, "state"]);
                                        }
                                    },
                                    placeholder: "Select...",
                                    style: {
                                        color: (
                                            (showErrors.value.includes("all") || showErrors.value.includes("state")) && 
                                            !data.value?.state && 
                                            "danger"
                                        ),
                                        size: "medium",
                                    },
                                    className: "no-ratio",
                                    xdQaName: "New Homeowners State",
                                })}
                            </div>
                        </div>
                        <div className="line">
                            <div className="label" xd-qa-type="label" xd-qa-name="New Homeowners City">City</div>
                            <div className="control">
                                {data.value?.state && SearchableSelect({
                                    list: allCities.value,
                                    isSelected: (c) => c === data.value.city,
                                    onSelect: (city) => {
                                        data.change((_) => ({
                                            ..._,
                                            city,
                                            zipCodes: [],
                                        }));
                                        apiError.onChange(null);
                                    },
                                    onClose: () => {
                                        if (!showErrors.value.includes("city") && !data.value?.city) {
                                            showErrors.onChange([...showErrors.value, "city"]);
                                        }
                                    },
                                    placeholder: allCities.loading ? "Loading..." : "Select...",
                                    style: {
                                        color: (
                                            (showErrors.value.includes("all") || showErrors.value.includes("city")) && 
                                            !data.value?.city &&
                                            "danger"
                                        ),
                                        // color: "danger",
                                        size: "medium",
                                    },
                                    className: "no-ratio",
                                    xdQaName: "New Homeowners Search City",
                                })}
                            </div>
                        </div>
                        <div className="line zipCodes">
                            <div className="label" xd-qa-type="label" xd-qa-name="New Homeowners Zip Codes">Zip Codes</div>
                            <div className="control">
                                {!allZipCodes.value ? (
                                    <div className="loading">
                                        {allZipCodes.loading? (
                                            "Loading zip codes..."
                                        ) : (
                                            ""
                                        )}
                                    </div>
                                ) : (
                                    ZipCodes({
                                        list: allZipCodes.value,
                                        isSelected: (z) => data.value.zipCodes?.includes(z),
                                        onSelect: (z) => {
                                            stateChangeNestedValue(data, ["zipCodes"], (_) => (
                                                _?.includes(z) ? (
                                                    _.filter((z1) => z1 !== z)
                                                ) : (
                                                    [..._??[], z]
                                                )
                                            ));
                                            apiError.onChange(null);
                                        },
                                        onSelectAll: () => {
                                            stateChangeNestedValue(data, ["zipCodes"], () => allZipCodes.value);
                                            apiError.onChange(null);
                                        },
                                        onUnselectAll: () => {
                                            stateChangeNestedValue(data, ["zipCodes"], () => []);
                                            apiError.onChange(null);
                                        },
                                        onClose: () => {
                                            if (!showErrors.value.includes("stateZipCodes") && !data.value.zipCodes?.length) {
                                                showErrors.onChange([...showErrors.value, "stateZipCodes"]);
                                            }
                                        },
                                        style: {color: (
                                            (showErrors.value.includes("all") || showErrors.value.includes("stateZipCodes")) && 
                                            !data.value.zipCodes?.length && 
                                            "danger"
                                        )},
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                    {apiError.value && (
                        <div className="error" xd-qa-name="New Homeowners By City Error" xd-qa-type="label">
                            {apiError.value.error}
                        </div>
                    )}
                </div>
                <div className="right">
                    <div className="clear">
                        <div className="control">
                            <a {...{
                                onClick: () => data.onChange({}),
                                "xd-qa-type": "link",
                                "xd-qa-name": "New Homeowners Reset"
                            }}>
                                Reset
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        )
    }),
);
